import enviro from 'enviro';
import { getFullUrl } from 'hubspot-url-utils';
import { useCallback, useRef } from 'react';
const hasDisableLocalCheckFlag = () => {
  try {
    return localStorage.getItem('NAV_COMPONENTS_SRC_NO_LOCAL') === 'true';
  } catch (e) {
    return false;
  }
};
const hasSeleniumLocalRunFlag = () => {
  try {
    return localStorage.getItem('NAV_COMPONENTS_SELENIUM_LOCAL_RUN') === 'true';
  } catch (e) {
    return false;
  }
};
const extraMsg = `Please feel free to disregard the error calling the 'https://localhost/bender-proxy/config' endpoint.
It only occurs in QA for Hubspotters.`;
const getPatterns = async () => {
  const benderProxyConfigUrl = 'https://localhost/bender-proxy/config';
  try {
    const response = await fetch(benderProxyConfigUrl, {
      method: 'GET'
    });
    const responseJson = await response.json();
    return responseJson.applied.flatMap(({
      routes
    }) => routes.map(({
      pattern
    }) => new RegExp(pattern.replace(/\{anyHost\}/g, '(https://)?[^/]+'))));
  } catch (e) {
    console.log(extraMsg);
    return [];
  }
};

/**
 * This function governs switching on/off the functionality that detects locally
 * running instances of Navigation Components apps running in bend.
 *
 * It is later used by `useSrcManager` to determine the right domain to be used
 * in underlying iframe's src.
 *
 * It's enabled when (all of):
 * - there's no `NAV_COMPONENTS_SRC_NO_LOCAL` local storage flag set to `true`
 * - host app is either running locally or in QA when user email is `@hubspot.com`
 * - host app isn't run by selenium or the `NAV_COMPONENTS_SELENIUM_LOCAL_RUN`
 *   local storage flag is set to `true`.
 *
 * @returns should check for local instances of navigation components apps running
 */
export const shouldCheckForLocalApps = () => {
  // Is there a local storage flag to disable local checks set to `true`?
  const disableLocalCheck = hasDisableLocalCheckFlag();
  // Is the host app running from public LB (e.g. `app.hubspot(qa)?.com`)?
  const isDeployed = enviro.deployed();
  // Is the host app running in QA
  const isQa = enviro.isQa();
  // Is the host app run by Selenium and for local runs resp. local storage flag isn't set to `true`?
  const isSeleniumInPipeline = document.cookie.includes('hs_selenium') && !hasSeleniumLocalRunFlag();
  return !disableLocalCheck && (!isDeployed || isQa) && !isSeleniumInPipeline;
};
export const useSrcManager = () => {
  const checkForLocalApps = shouldCheckForLocalApps();
  const patterns = useRef(null);
  const cache = useRef({}).current;
  const isRunningLocally = useCallback(async path => {
    const url = `${getFullUrl('local')}${path}`;
    if (cache[path]) {
      return cache[path];
    }
    if (!patterns.current) {
      patterns.current = getPatterns();
    }
    const currentPatterns = await patterns.current;
    cache[path] = !!currentPatterns.find(rx => rx.test(url));
    return cache[path];
  }, [cache]);
  return useCallback(async (path, qp) => {
    // Unit tests don't actually load the iframe, src can be empty
    if ('jasmine' in window) {
      return '';
    }
    const fmtPath = !path.startsWith('/') ? `/${path}` : path;
    const shouldUseLocalEnv = checkForLocalApps ? await isRunningLocally(fmtPath) : false;
    const subdomain = shouldUseLocalEnv ? 'local' : 'app';
    const fqdn = getFullUrl(subdomain);
    if (qp) {
      const params = typeof qp === 'function' ? await qp() : qp;
      const urlSearchParams = new URLSearchParams(params);
      return `${fqdn}${fmtPath}?${urlSearchParams.toString()}`;
    }
    return `${fqdn}${fmtPath}`;
  }, [isRunningLocally, checkForLocalApps]);
};